import { Scanner } from "@yudiel/react-qr-scanner";
import { useState, useEffect } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { FlipHorizontal } from "lucide-react";

const QRScanner = ({ show, handleClose, onResult }: any) => {
  const [cameraConstraints, setCameraConstraints] = useState<MediaStreamConstraints>({
    video: { facingMode: "environment" } // Start with back camera
  });
  const [hasTriedFrontCamera, setHasTriedFrontCamera] = useState(false);
  const [currentCamera, setCurrentCamera] = useState<"environment" | "user">("environment");
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  const [scannerKey, setScannerKey] = useState(0); // Key to force Scanner remount

  // Reset camera preference when modal opens
  useEffect(() => {
    if (show) {
      setCameraConstraints({
        video: { facingMode: "environment" } // Try back camera first
      });
      setCurrentCamera("environment");
      setHasTriedFrontCamera(false);
      setScannerKey(0); // Reset key
      setIsSwitchingCamera(false);
    }
  }, [show]);

  // Toggle between front and back camera
  const toggleCamera = () => {
    setIsSwitchingCamera(true);
    
    const newCamera = currentCamera === "environment" ? "user" : "environment";
    setCurrentCamera(newCamera);
    
    // Update constraints with new facing mode
    setCameraConstraints({
      video: { facingMode: newCamera }
    });
    
    // Force Scanner to remount by changing the key - this ensures the old stream is stopped
    setScannerKey(prev => prev + 1);
    
    // Reset switching state after Scanner remounts
    setTimeout(() => {
      setIsSwitchingCamera(false);
    }, 300);
  };

  const handleScan = (result: any) => {
    if (result && result.length > 0) {
      const raw = result[0].rawValue;
      let finalValue: any = raw;

      try {
        finalValue = JSON.parse(raw); // parse JSON if possible
        toast.success(`✅ QR Scanned! User: ${finalValue.userId || "N/A"}`);
      } catch {
        // QR code is not JSON, use raw value directly
      }

      onResult?.(finalValue);
      handleClose();
    }
  };

  const handleError = (error: any) => {
    console.error("QR Scan Error:", error);
    // If back camera fails and we haven't tried front camera yet, fallback to front
    if (!hasTriedFrontCamera && cameraConstraints.video && typeof cameraConstraints.video === 'object' && cameraConstraints.video.facingMode === 'environment') {
      setCameraConstraints({
        video: { facingMode: "user" } // Fallback to front camera
      });
      setCurrentCamera("user");
      setHasTriedFrontCamera(true);
    } else {
      toast.error("❌ QR Scan failed. Please try again.");
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      contentClassName="custom-modal"
    >
      <Modal.Header className="border-0">
        <Modal.Title className="fw-bold">Scan QR Code</Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleClose}
        ></button>
      </Modal.Header>
      <Modal.Body>
        <div style={{ width: "100%", height: "300px", position: "relative" }}>
          {isSwitchingCamera ? (
            <div 
              style={{ 
                width: "100%", 
                height: "100%", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                backgroundColor: "#f8f9fa",
                borderRadius: "10px"
              }}
            >
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Scanner
              key={scannerKey} // Force remount when camera changes
              onScan={handleScan}
              onError={handleError}
              constraints={cameraConstraints}
              styles={{
                container: { width: "100%", height: "100%" },
                video: { width: "100%", borderRadius: "10px" },
              }}
            />
          )}
          {/* Camera switch button - visible on mobile and tablet */}
          <Button
            onClick={toggleCamera}
            disabled={isSwitchingCamera}
            variant="light"
            className="d-xl-none"
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              borderRadius: "50%",
              width: "45px",
              height: "45px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              border: "none",
              zIndex: 10,
              opacity: isSwitchingCamera ? 0.6 : 1,
            }}
            title={`Switch to ${currentCamera === "environment" ? "front" : "back"} camera`}
          >
            <FlipHorizontal size={20} color="#333" />
          </Button>
        </div>
        {/* {scannedValue && (
          <div className="text-center mt-3 fw-bold text-success">
            ✅ userId: {scannedValue.userId}
          </div>
        )} */}
        <div className="d-flex justify-content-center gap-3 mt-4">
          <Button
            variant="outline-secondary"
            style={{
              color: "#878899",
              borderColor: "#B0BAC9",
              backgroundColor: "transparent",
            }}
            onClick={handleClose}
          >
            Cancel
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default QRScanner;
